(() => {
  if (customElements.get('aplovio-related-products')) return;
  const requests = new Map();
  window.AplovioCarouselIsGrid = carousel => {
    const mobile = matchMedia('(max-width:749px)').matches;
    const layout = mobile && carousel.dataset.mobileLayout && carousel.dataset.mobileLayout !== 'inherit' ? carousel.dataset.mobileLayout : carousel.dataset.layout;
    return layout === 'grid';
  };
  function imageReady(image, signal) {
    return new Promise((resolve, reject) => {
      let finished = false;
      let decoding = false;
      const finish = failure => {
        if (finished) return;
        finished = true;
        image.removeEventListener('load', check);
        image.removeEventListener('error', check);
        signal.removeEventListener('abort', cancel);
        if (failure) reject(failure);
        else resolve();
      };
      const cancel = () => finish(signal.reason);
      const check = async () => {
        if (!image.complete || decoding || finished) return;
        decoding = true;
        if (image.naturalWidth && image.decode) {
          try { await image.decode(); }
          catch { /* A replaced or failed image must not hide otherwise usable card links. */ }
        }
        finish();
      };
      image.addEventListener('load', check);
      image.addEventListener('error', check);
      signal.addEventListener('abort', cancel, {once: true});
      if (signal.aborted) cancel();
      else check();
    });
  }
  // One geometry calculation for pending skeletons and active carousels.
  window.AplovioCarouselLayout = (carousel, count) => {
    const track = carousel.querySelector('[data-carousel-track]');
    const contentWidth = carousel.clientWidth;
    if (contentWidth <= 0 || !count) return null;
    const minimum = {compact:140, standard:200, large:280}[carousel.dataset.cardSize] || 200;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 16;
    const mobile = matchMedia('(max-width:749px)').matches;
    const grid = window.AplovioCarouselIsGrid(carousel);
    carousel.toggleAttribute('data-grid', grid);
    const bleed = !grid && mobile && carousel.dataset.mobileBleed !== 'false';
    const scrollOwner = carousel.closest('[data-ap-scroll-owner]');
    const viewportWidth = scrollOwner?.clientWidth || document.documentElement.clientWidth;
    const viewportLeft = scrollOwner ? scrollOwner.getBoundingClientRect().left + scrollOwner.clientLeft : 0;
    const bounds = carousel.getBoundingClientRect();
    const rtl = getComputedStyle(carousel).direction === 'rtl';
    const left = Math.max(0, bounds.left - viewportLeft);
    const right = Math.max(0, viewportLeft + viewportWidth - bounds.right);
    const start = bleed ? (rtl ? right : left) : 3;
    const end = bleed ? (rtl ? left : right) : 3;
    carousel.style.setProperty('--ap-carousel-viewport-width', `${viewportWidth}px`);
    carousel.style.setProperty('--ap-carousel-inset-start', `${start}px`);
    carousel.style.setProperty('--ap-carousel-inset-end', `${end}px`);
    const maximum = Number(mobile ? carousel.dataset.mobileColumns : carousel.dataset.columns) || 4;
    const fitting = mobile ? maximum : Math.floor((contentWidth + gap) / (minimum + gap)) || 1;
    // Keep the chosen row density when there are fewer products than slots.
    const columns = grid ? maximum : Math.max(1, Math.min(maximum, fitting));
    const more = !grid && count > columns;
    const width = bleed && more ? viewportWidth - start : contentWidth;
    const peek = more && carousel.dataset.peek !== 'false' ? Math.min(44, width * .12) : 0;
    const calculated = (width - gap * (columns - 1) - peek) / columns;
    track.style.setProperty('--ap-carousel-card-width', `${calculated}px`);
    carousel.dataset.fit = String(!more);
    return {columns, more, start, end, grid};
  };
  const skeletonResize = new ResizeObserver(entries => {
    for (const entry of entries) {
      const root = entry.target.closest('aplovio-related-products');
      if (root?.matches('[data-pending], [data-static-fallback]')) window.AplovioCarouselLayout(entry.target, Number(root.dataset.expectedCount));
    }
  });
  function acquire(url) {
    let request = requests.get(url.href);
    if (!request) {
      const abort = new AbortController();
      const timer = setTimeout(() => abort.abort(), 12000);
      request = {abort, users: 0};
      request.promise = window.AplovioLazy.read(url, {credentials:'same-origin', signal:abort.signal, headers:{Accept:'text/html'}})
        .then(async response => {
          const destination = new URL(response.url);
          if (!response.ok || destination.origin !== url.origin || destination.pathname !== url.pathname || !response.headers.get('content-type')?.includes('text/html')) throw new Error('Invalid carousel response');
          const html = await response.text();
          if (html.length > 5000000) throw new Error('Carousel response too large');
          return html;
        }).finally(() => {clearTimeout(timer); if (requests.get(url.href) === request) requests.delete(url.href);});
      requests.set(url.href, request);
    }
    request.users++;
    let released = false;
    return {promise: request.promise, release() {
      if (released) return;
      released = true; request.users--;
      if (!request.users) {request.abort.abort(); if (requests.get(url.href) === request) requests.delete(url.href);}
    }};
  }

  customElements.define('aplovio-related-products', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const track = this.querySelector('[data-carousel-track]');
      const carousel = track?.closest('aplovio-carousel');
      if (carousel) carousel.toggleAttribute('data-grid', window.AplovioCarouselIsGrid(carousel));
      if (track && this.matches('[data-pending], [data-static-fallback]')) skeletonResize.observe(track.closest('aplovio-carousel'));
      this.querySelector('[data-carousel-retry]')?.addEventListener('click', () => this.activate(), {signal:this.events.signal});
      this.addEventListener('shopify:block:select', () => this.activate(), {signal:this.events.signal});
      if (this.dataset.loading === 'near' && !window.Shopify?.designMode) window.AplovioLazy.near(this, () => this.activate(), {signal: this.events.signal});
      else this.activate();
    }
    async activate() {
      if (this.active || this.loading || this.hidden) return;
      this.loading = true;
      const generation = (this.generation || 0) + 1; this.generation = generation;
      const route = location.pathname + location.search;
      const error = this.querySelector('[data-carousel-error]');
      let lease, content;
      if (error) error.hidden = true;
      this.setAttribute('aria-busy', 'true');
      try {
        let track = this.querySelector('[data-carousel-track]');
        if (!track) {this.active = true; return;}
        let recentURL;
        if (this.dataset.remote === 'recent') {
          if (this.dataset.recentEnabled === 'false') {this.hidden = true; this.active = true; return;}
          await window.AplovioLazy.asset(this.dataset.recentRuntime, 'script');
          if (!this.current(generation, route)) return;
          const history = window.AplovioRecentProducts.entries(this.dataset.productId, this.dataset.limit);
          if (!history.length) {this.hidden = true; this.active = true; return;}
          this.dataset.expectedCount = String(history.length);
          recentURL = new URL(this.dataset.searchUrl, location.href);
          recentURL.searchParams.set('q', 'aplovio-recent:|' + history.map(item => item.handle).join('|'));
          recentURL.searchParams.set('type', 'product');
        }
        const grid = window.AplovioCarouselIsGrid(this.querySelector('aplovio-carousel'));
        const runtime = Promise.all([
          window.AplovioLazy.asset(this.dataset.runtime, 'script', 'aplovio-carousel'),
          grid ? null : window.AplovioLazy.asset(this.dataset.runtimeCss, 'style'),
          grid ? null : window.AplovioLazy.component(this.querySelector('[data-carousel-indicator]:not([data-type="none"])'))
        ]);
        runtime.catch(() => {}); // Awaited below; handle rejection while a native response is pending.
        if (this.dataset.remote) {
          const url = recentURL || (this.dataset.remote === 'recommendations' ? new URL(this.dataset.url, location.href) : new URL(location.href));
          if (url.origin !== location.origin) throw new Error('Invalid carousel destination');
          if (this.dataset.remote !== 'recommendations') url.searchParams.set('section_id', this.dataset.sectionId);
          url.hash = '';
          lease = acquire(url); this.request = lease;
          const html = await lease.promise;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          // Modal sections keep their blocks inert until the modal opens.
          const scopes = [doc, ...[...doc.querySelectorAll('template[data-section-content]')].map(template => template.content)];
          const source = scopes.flatMap(scope => [...scope.querySelectorAll('aplovio-related-products')]).find(node => node.dataset.key === this.dataset.key && node.dataset.sectionId === this.dataset.sectionId);
          if (!source || source.dataset.remote) throw new Error('Carousel response did not match this block');
          content = source.querySelector('[data-carousel-track]');
          if (content && this.dataset.productId) {
            for (const item of content.querySelectorAll('[data-carousel-item]')) {
              if (item.dataset.itemId === this.dataset.productId) item.remove();
            }
          }
        } else content = this.querySelector('template[data-carousel-content]')?.content;
        await runtime;
        if (!this.current(generation, route)) return;
        if ((content || track).querySelector('[data-card-image-sources]')) {
          await Promise.all([
            window.AplovioLazy.asset(this.dataset.mediaRuntime, 'script', 'aplovio-card-media'),
            window.AplovioLazy.component((content || track).querySelector('aplovio-indicators:not([data-type="none"][data-mobile-type="none"])')),
            (content || track).querySelector('[data-card-image-track]') ? window.AplovioLazy.asset(this.dataset.mediaCss, 'style') : null
          ]);
        }
        if (!this.current(generation, route)) return;
        if (content) {
          this.mountCards(track, content, this.hasAttribute('data-pending'));
        } else if (this.dataset.remote) track.replaceChildren();
        if (this.hasAttribute('data-pending')) {
          const signal = AbortSignal.any([this.events.signal, AbortSignal.timeout(8000)]);
          await Promise.all(this.primeImages(track).map(image => imageReady(image, signal)));
          if (!this.current(generation, route)) return;
          this.revealCards(track);
        }
        this.removeAttribute('data-pending');
        this.removeAttribute('data-static-fallback');
        skeletonResize.unobserve(track.closest('aplovio-carousel'));
        this.querySelector('[data-carousel-loading]')?.remove();
        delete this.dataset.remote; delete this.dataset.url;
        track.tabIndex = 0;
        this.active = true;
        this.hidden = !track.querySelector('[data-carousel-item]') && !window.Shopify?.designMode;
        const carousel = this.querySelector('aplovio-carousel');
        carousel?.activate();
        carousel?.schedule(true);
        document.dispatchEvent(new CustomEvent('aplovio:recommendations-loaded'));
      } catch (failure) {
        if (!this.current(generation, route)) return;
        if (content?.querySelector('[data-carousel-item]')) {
          const track = this.querySelector('[data-carousel-track]');
          this.mountCards(track, content);
          this.removeAttribute('data-pending');
          this.querySelector('[data-carousel-loading]')?.remove();
          track.tabIndex = 0;
          this.setAttribute('data-static-fallback', '');
        }
        if (error) error.hidden = false;
        console.warn('[Aplovio] Carousel could not load. Retry is available.');
      } finally {
        lease?.release();
        if (this.request === lease) this.request = null;
        if (this.generation === generation) {this.loading = false; this.setAttribute('aria-busy', 'false');}
      }
    }
    primeImages(track) {
      const carousel = this.querySelector('aplovio-carousel');
      const items = [...track.querySelectorAll('[data-carousel-item]')];
      this.dataset.expectedCount = String(items.length);
      const layout = window.AplovioCarouselLayout(carousel, items.length);
      // Prepare the first viewport plus its peek while still inside the near-viewport margin.
      const visible = (layout?.columns || 1) + (layout?.more && carousel.dataset.peek !== 'false' ? 1 : 0);
      const ready = [];
      items.forEach((item, index) => {
        const images = item.querySelectorAll('.ap-card-image-track > a:first-child img:not(.ap-card-media__second), .ap-shop-card__image-link img');
        for (const image of images) {
          image.loading = index < visible ? 'eager' : 'lazy';
          if (index < visible) ready.push(image);
        }
      });
      return ready;
    }
    revealCards(track) {
      for (const item of track.querySelectorAll('[data-carousel-loading-item]')) {
        item.removeAttribute('data-carousel-loading-item');
        item.removeAttribute('aria-hidden');
        item.inert = false;
        item.querySelector(':scope > [data-card-skeleton]')?.removeAttribute('data-card-skeleton');
      }
    }
    mountCards(track, content, pending = false) {
      if (pending) {
        for (const item of content.querySelectorAll('[data-carousel-item]')) {
          item.setAttribute('data-carousel-loading-item', '');
          item.setAttribute('aria-hidden', 'true');
          item.inert = true;
          item.querySelector(':scope > .ap-product-card, :scope > .ap-shop-card')?.setAttribute('data-card-skeleton', '');
        }
      }
      // Parsed scripts do not execute; this owner loads the shared runtime exactly once.
      for (const script of content.querySelectorAll('script[src]')) {
        if (new URL(script.src, location.href).pathname.endsWith('/aplovio-card-media.js')) script.remove();
      }
      for (const style of content.querySelectorAll('link[rel=stylesheet]')) {
        if (new URL(style.href, location.href).pathname.endsWith('/aplovio-card-gallery.css')) style.remove();
      }
      track.replaceChildren(...content.childNodes);
      this.querySelector('template[data-carousel-content]')?.remove();
      if (!pending) this.primeImages(track);
    }
    current(generation, route) {return this.isConnected && this.generation === generation && route === location.pathname + location.search;}
    disconnectedCallback() {
      this.events?.abort(); this.events = null;
      const track = this.querySelector('[data-carousel-track]');
      if (track) skeletonResize.unobserve(track.closest('aplovio-carousel'));
      this.generation = (this.generation || 0) + 1; this.loading = false;
      this.request?.release(); this.request = null;
    }
  });
})();
